import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import axios from 'axios'
import { KTCard, KTCardBody, ModalSize } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  Button,
  CustomTabs,
  ListLoading,
  StatusBadge,
  TabItem,
  TableErrorState,
  useModal,
} from 'app/components'
import { rotuloMaleta, rotuloRegime } from '../../dispositivos/core/rotulos'
import { getTelekitById } from '../telekits-list/core/_requests'
import { useRecarregaAoFecharModal } from '../../dispositivos/core/useRecarregaAoFecharModal'
import { TelekitDispositivosTab } from './dispositivos/TelekitDispositivosTab'
import { TelekitHistoricoTab } from './dispositivos/TelekitHistoricoTab'
import { TransferirCustodiaModal } from './TransferirCustodiaModal'

type Aba = 'dispositivos' | 'historico'

export const TelekitDetalhe = () => {
  const { t } = useT()
  const { id } = useParams()
  const idTelekit = Number(id)
  const [busca, setBusca] = useSearchParams()
  const aba: Aba = busca.get('aba') === 'historico' ? 'historico' : 'dispositivos'
  const { showModal } = useModal()

  const {
    data: telekit,
    isLoading,
    error,
    refetch,
  } = useQuery(['telekit-detalhe', idTelekit], () => getTelekitById(idTelekit), {
    enabled: Number.isFinite(idTelekit),
    refetchOnWindowFocus: false,
    retry: false,
  })
  useRecarregaAoFecharModal(refetch)

  const voltar = (
    <Link to='/apps/admin/telekits' className='fw-semibold'>
      {t('pagina.telekits.dispositivos.voltar')}
    </Link>
  )

  if (isLoading && !telekit) return <ListLoading />

  if (error || !telekit) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined
    return (
      <KTCard>
        <KTCardBody>
          <TableErrorState
            title={t(
              status === 404
                ? 'pagina.telekits.dispositivos.nao_encontrado'
                : 'pagina.dispositivos.detalhe.erro'
            )}
            description={voltar}
            onRetry={status === 404 ? undefined : () => refetch()}
          />
        </KTCardBody>
      </KTCard>
    )
  }

  const nome = rotuloMaleta(t, { identificador: telekit.identificador, padrao: telekit.padrao })
  const abas: TabItem<Aba>[] = [
    { id: 'dispositivos', label: t('pagina.telekits.dispositivos.aba') },
    { id: 'historico', label: t('pagina.telekits.dispositivos.aba_historico') },
  ]

  const abrirCustodia = () =>
    showModal({
      title: t('pagina.telekits.custodia.titulo'),
      description: nome,
      size: ModalSize.Medium,
      content: <TransferirCustodiaModal />,
      // Mesma maleta que a página acabou de carregar: reaproveita, sem novo GET.
      fetchers: { telekit: () => Promise.resolve(telekit) },
    })

  return (
    <KTCard>
      <KTCardBody>
        <div className='mb-4'>{voltar}</div>
        <div className='d-flex flex-wrap align-items-start justify-content-between gap-4 mb-6'>
          <div>
            <h2 className='fs-3 fw-bold text-gray-900 mb-2'>{nome}</h2>
            <div className='d-flex flex-wrap align-items-center gap-3 text-gray-700'>
              <StatusBadge active={telekit.ativo} labels={['Ativa', 'Inativa']} />
              <span>{telekit.empresa?.nomeFantasia ?? '—'}</span>
              <span>
                {t('pagina.telekits.dispositivos.regime', {
                  regime: rotuloRegime(t, telekit.regime ?? 'LAUDUZ'),
                })}
              </span>
            </div>
          </div>
          <Button variant='secondary-outline' onClick={abrirCustodia}>
            {t('pagina.telekits.custodia.acao')}
          </Button>
        </div>

        <CustomTabs<Aba>
          tabs={abas}
          activeTab={aba}
          onChange={(nova) => setBusca({ aba: nova })}
          className='mb-6'
          tabClassName='fs-5 fw-semibold text-gray-700'
          activeTabClassName='text-gray-900 fw-bolder'
        />

        {aba === 'dispositivos' && <TelekitDispositivosTab telekit={telekit} />}
        {aba === 'historico' && <TelekitHistoricoTab idTelekit={idTelekit} />}
      </KTCardBody>
    </KTCard>
  )
}
